import {
    Component,
    computed,
    effect,
    inject,
    input,
    output,
    resource,
    untracked,
    ChangeDetectionStrategy,
} from "@angular/core";
import { CalendarService } from "../../modules/home/calendar/calendar.service";
import { CalendarData } from "../../modules/home/calendar/CalendarData";
import { S25IconComponent } from "../../modules/s25-icon/s25.icon.component";
import { S25Icons } from "../../modules/s25-icon/s25.icon.const";
import { S25LoadingInlineModule } from "../../modules/s25-loading/s25.loading.inline.module";
import { OptBean } from "../../modules/s25-opt/s25.opt.component";
import { OptUtil, OptUtilI } from "../../modules/s25-opt/s25.opt.util";
import { Item } from "../../pojo/Item";
import { UserStore } from "../../stores/user.store";
import { S25Util } from "../../util/s25-util";
import { s25Resource } from "../../util/s25.resource";
import { S25CalendarStore } from "./calendar.store";
import { CalendarTable } from "./calendar.table.component";
import type { CalendarOptModelBean } from "./calendar.with.options.component";
import { HttpRequestOptions } from "../../dataaccess/data.access";

export type TempSearch = {
    invalidSearch: boolean;
    searchQuery: string;
    searchDelete: () => Promise<void>;
};
/** Creates the server-side search behind an advanced search, to be deleted once the results are in. */
export type TempSearchFn = (httpOptions?: HttpRequestOptions) => Promise<TempSearch>;

type CalendarLoadParams = CalendarService.DataParams & {
    showBlackouts: boolean;
    dataFn: NonNullable<CalendarOptModelBean["getdata"]>;
    tempSearchFn: TempSearchFn | undefined;
};

@Component({
    selector: "s25-ng-calendar",
    standalone: true,
    imports: [CalendarTable, S25LoadingInlineModule, S25IconComponent],
    host: {
        class: "ngCalendarContainer ngCalendarPadding",
    },
    styles: `
        :host {
            display: block;
            width: 100%;
            font-size: 0.875rem;
        }
        .loading-indicator {
            display: flex;
            width: 100%;
            justify-content: center;
            font-size: 150%;
            padding: 0 1em 1em;
        }
        .ngCalArrowRight,
        .ngCalArrowLeft {
            cursor: pointer;
            margin: 0.5em auto;
            height: 2em;
            padding: 0 0.75em;
        }
        .ngCalArrowRight {
            float: right;
        }
        :host-context(.nm-party--on) :host {
            .ngCalArrowRight,
            .ngCalArrowLeft {
                background-color: transparent;
                border-color: var(--color--link-text);
                color: var(--color--link-text);
                cursor: pointer;

                &:hover,
                &:focus {
                    background-color: var(--color-primary-darker) !important;
                    color: var(--color--link-text-hover) !important;
                }
                &:hover {
                    border-color: transparent !important;
                }
                &:focus {
                    border-color: var(--color--link-text-hover) !important;
                }
            }
        }
    `,
    changeDetection: ChangeDetectionStrategy.Eager,
    template: `
        <!-- TODO: render empty cells with skeleton loaders -->
        @if (isLoading() || rawCalData.isLoading()) {
            <s25-ng-loading-inline-static class="loading-indicator" text="Calendar Loading..." />
        }
        @if (calData()?.events) {
            <s25-ng-calendar-table
                [events]="calData().events"
                [weekdays]="calData().weekday"
                [daysPerRow]="calData().days_per_row"
                [date]="date()"
            />
            <button
                class="btn btn-default ngOptArrowLeft ngCalArrowLeft"
                type="button"
                (click)="previousPage.emit()"
                aria-label="Update Date Backwards"
            >
                <s25-ng-icon [type]="icons.arrowLeft" [ariaLabel]="false" />
            </button>
            <button
                class="btn btn-default ngOptArrowRight ngCalArrowRight"
                type="button"
                (click)="nextPage.emit()"
                aria-label="Update Date Forwards"
            >
                <s25-ng-icon [type]="icons.arrowRight" [ariaLabel]="false" />
            </button>
        }
        @if (calMessage()) {
            <div class="NoInformation">
                <span>{{ calMessage() }}</span>
            </div>
        }
    `,
})
export class Calendar {
    readonly comptype = input.required<OptUtil.CompType>();
    readonly compsubject = input.required<Item.Subject>();
    readonly date = input.required<Date>();
    readonly searchQuery = input<string>();
    readonly dataFn = input<CalendarOptModelBean["getdata"]>();
    readonly tempSearchFn = input<TempSearchFn>();
    readonly showBlackouts = input<boolean>(false);
    readonly multiQueryStr = input<OptBean["multiQueryStr"]>();
    readonly useCache = input<OptBean["useCache"]>();
    readonly includeAddtl = input<OptBean["includeAddtl"]>();
    readonly showRelatedEvents = input<OptBean["showRelatedEvents"]>();
    readonly showRelatedLocations = input<OptBean["showRelatedLocations"]>();
    readonly endDate = input<OptBean["endDate"]>();
    readonly datePropertyValue = input<OptUtilI["datePropertyValue"]>();
    readonly chosenVal = input<OptBean["chosen"]["obj"]["val"]>();
    readonly isLoading = input<boolean>(false);

    readonly previousPage = output<void>();
    readonly nextPage = output<void>();
    readonly updated = output<OptUtilI["lastupdate"]>();

    readonly store = inject(S25CalendarStore);
    readonly user = inject(UserStore);

    readonly calMessage = computed(() => {
        if (this.rawCalData.hasValue() && !this.rawCalData.value()?.events?.length) {
            return "No search results were found.";
        }
    });

    // TODO: consider adding a sticky function that preserves the previous value while loading
    //  like in https://www.reddit.com/r/Angular2/comments/1kwr7db/resource_keep_previous_value_helper/
    //  if we want to reduce visual flickering by showing the old calendar data while loading new data
    readonly rawCalData = resource({
        params: () => {
            if (this.isLoading()) return undefined;
            const weekstart = this.user.weekstart();
            if (S25Util.isUndefined(weekstart)) return undefined;
            return {
                modelBean: {
                    searchQuery: this.searchQuery(),
                    optBean: {
                        multiQueryStr: this.multiQueryStr(),
                        useCache: untracked(this.useCache),
                        compsubject: this.compsubject(),
                        includeAddtl: this.includeAddtl(),
                        showRelatedEvents: this.showRelatedEvents(),
                        showRelatedLocations: this.showRelatedLocations(),
                        date: this.date(),
                        endDate: this.endDate(),
                        datePropertyValue: this.datePropertyValue(),
                        chosen: {
                            obj: {
                                val: this.chosenVal(),
                            },
                        },
                    },
                },
                weekstart,
                comptype: this.comptype(),
                showBlackouts: this.showBlackouts(),
                dataFn: this.dataFn() ?? CalendarService.getData,
                tempSearchFn: this.tempSearchFn(),
            };
        },
        loader: ({ params, abortSignal }) => Calendar.loadWithBlackouts(params, abortSignal),
    });
    readonly hasMore = s25Resource({
        params: () => {
            if (
                this.rawCalData.hasValue() &&
                this.rawCalData.value().obj_id &&
                this.rawCalData.value().events?.length
            ) {
                return {
                    weekstart: this.user.weekstart(),
                    modelBean: {
                        optBean: {
                            compsubject: this.compsubject(),
                            date: this.date(),
                            endDate: this.endDate(),
                        },
                        data: { root: { root: { obj_id: this.rawCalData.value().obj_id } } },
                    },
                };
            }
        },
        loader: ({ params }) => CalendarService.getHasMore(params),
    });
    readonly calData = computed(() => {
        if (this.rawCalData.hasValue()) {
            return this.applyHasMore(this.rawCalData.value());
        }
    });

    protected readonly icons = S25Icons.names;

    constructor() {
        effect(() => {
            if (this.rawCalData.hasValue()) {
                this.updated.emit(this.rawCalData.value().lastupdate);
            }
        });
        effect(() => this.store.setCalData(this.calData()));
        effect(() => {
            const error = this.rawCalData.error();
            if (error) {
                console.error(error);
                // keep UX minimal without S25LoadingApi, but surface error
                S25Util.showError(error, "There was an error loading the calendar, please try again later.");
            }
        });
    }

    /**
     * Add HasMore data to calData, without mutating the source
     */
    private applyHasMore(rawCalData: CalendarData): CalendarData {
        if (this.hasMore.value() && rawCalData.events?.length) {
            const hasMore = this.hasMore.value()?.hasMore;
            if (hasMore?.length !== rawCalData.events.length) {
                console.error("Unexpected mismatch between hasMore and events length");
                return rawCalData;
            }
            return {
                ...rawCalData,
                events: rawCalData.events.map((event, i) => ({
                    ...event,
                    hasMore: this.hasMore.value()?.hasMore[i] ?? 0,
                })),
            };
        } else {
            return rawCalData;
        }
    }

    private static async loadWithBlackouts(
        params: CalendarLoadParams,
        abortSignal: AbortSignal,
    ): Promise<CalendarData> {
        const httpOptions: HttpRequestOptions = { abortSignal, throwErrorClass: true };
        const tempSearch = await params.tempSearchFn?.(httpOptions);
        if (tempSearch?.invalidSearch) {
            void tempSearch.searchDelete();
            throw new Error("Invalid search " + (tempSearch.searchQuery ?? ""));
        }

        // CalendarService.defaultParams mutates modelBean.searchQuery, so each request needs its own copy
        // TODO: make it not mutate the search query
        const requestParams = (): CalendarService.DataParams => ({
            ...params,
            modelBean: {
                ...params.modelBean,
                searchQuery: tempSearch?.searchQuery ?? params.modelBean.searchQuery,
            },
        });

        try {
            const [calData, blackouts] = await Promise.all([
                params.dataFn(requestParams(), httpOptions),
                params.showBlackouts ? CalendarService.getBlackouts(requestParams()) : undefined,
            ]);
            if (blackouts) CalendarService.setBlackouts(blackouts, calData.root, true);
            return calData.root;
        } finally {
            void tempSearch?.searchDelete();
        }
    }

    public refresh() {
        this.rawCalData.reload();
    }
}
